/**
 * ExecutionLog — ЗӨРЧЛИЙН ГҮЙЦЭТГЭЛ / ХЯНАЛТЫН БҮРТГЭЛ.
 *
 * ⚠ ЯАГААД ХЭРЭГТЭЙ ВЭ:
 *   Хуучин зөрчлийн бүртгэлд (`Inspection.Register` + `Execution` +
 *   `Approve`) байсан ДӨРВӨН зүйл шинэ бүтцэд шилжээгүй байв:
 *     ① ГҮЙЦЭТГЭЛИЙН ХУВЬ (0–100). Шинэд зөвхөн төлөв (NEW /
 *        IN_PROGRESS / COMPLETED) байсан тул «30% хийгдсэн» ба «90%
 *        хийгдсэн» хоёр ижилхэн харагдана.
 *     ② ОЛОН УДААГИЙН гүйцэтгэл. Шинэд `response_action` НЭГ текст —
 *        хоёр дахь удаа засварлавал эхнийхийг ДАРЖ БИЧНЭ.
 *     ③ ХЯНАСАН түүх.
 *     ④ «ДАХИН ГҮЙЦЭТГЭХ» буцаалт — хянагч «дутуу байна, дахин хий»
 *        гэж буцаах алхам огт байхгүй байв.
 *
 * ⚠ ХУВЬ нь ЗӨВХӨН сүүлийн гүйцэтгэлээс. Хамгийн их хувийг авбал
 *   буцаагдсан ажил 100% хэвээр харагдана.
 *
 * ⚠ ГҮЙЦЭТГЭЛГҮЙ зөрчлийг ХЯНАХ боломжгүй — сервер тал ч хориглоно.
 *
 * Backend: /api/mobile/hazard/execution (GET · POST · DELETE)
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Modal,
  Slider,
  Tag,
  Tooltip,
  message,
} from "antd";

import {
  listHazardExecution,
  addHazardExecution,
  deleteHazardExecution,
} from "src/api/hazard";

const { TextArea } = Input;

const TYPE_META = {
  EXEC: { label: "Гүйцэтгэл", color: "#0EA5E9", icon: "fa-wrench" },
  APPROVE: { label: "Хянасан", color: "#16A34A", icon: "fa-check-circle" },
  REJECT: { label: "Дахин гүйцэтгэх", color: "#DC2626", icon: "fa-undo" },
};

/** Явцын зурвас — хувь нь өнгөөрөө ч ялгарна. */
function Bar({ percent }) {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  const color = p >= 100 ? "#16A34A" : p >= 50 ? "#0EA5E9" : "#D97706";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          flex: 1,
          height: 6,
          background: "#EEF1F5",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <div style={{ width: `${p}%`, height: "100%", background: color }} />
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color,
          fontVariantNumeric: "tabular-nums",
          minWidth: 34,
          textAlign: "right",
        }}
      >
        {p}%
      </span>
    </div>
  );
}

export default function ExecutionLog({ reportId, myTn, readonly }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);

  // Нэмэх модал — { type: "EXEC"|"APPROVE"|"REJECT" }
  const [adding, setAdding] = useState(null);
  const [percent, setPercent] = useState(50);
  const [desc, setDesc] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!reportId) return;
    setLoading(true);
    listHazardExecution(reportId)
      .then((d) => {
        setItems(d?.items || []);
        setSummary(d?.summary || null);
        setUnavailable(!!d?.unavailable);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [reportId]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = (type) => {
    setAdding(type);
    // Шинэ гүйцэтгэл нь өмнөх хувиас эхэлнэ — ахиц бичихэд хялбар
    setPercent(summary?.percent ?? 0);
    setDesc("");
    setNote("");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const d = await addHazardExecution({
        report_id: reportId,
        entry_type: adding,
        status_percent: adding === "EXEC" ? percent : undefined,
        exec_description: desc,
        comment: note,
      });
      setItems(d?.items || []);
      setSummary(d?.summary || null);
      setAdding(null);
      message.success("Бүртгэгдлээ");
    } catch (e) {
      // ⚠ Сервер «гүйцэтгэлгүй зөрчлийг хянах боломжгүй» гэх мэт
      //   ТОДОРХОЙ шалтгаан буцаадаг — түүнийг нь ил гаргана.
      message.error(
        e?.response?.data?.message || "Хадгалж чадсангүй — дахин оролдоно уу",
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = (row) =>
    Modal.confirm({
      title: "Бичилтийг устгах уу?",
      content: row.exec_description || row.comment || "",
      okText: "Устгах",
      cancelText: "Болих",
      okButtonProps: { danger: true },
      onOk: () =>
        deleteHazardExecution(row.id)
          .then((d) => {
            setItems(d?.items || []);
            setSummary(d?.summary || null);
            message.success("Устгалаа");
          })
          .catch((e) =>
            message.error(
              e?.response?.data?.message || "Устгаж чадсангүй",
            ),
          ),
    });

  if (unavailable) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ fontSize: 12 }}
        message="Гүйцэтгэлийн бүртгэл бэлэн болоогүй"
        description={
          <span>
            <code>Models/mobile/create_hazard_execution.sql</code> миграцыг
            SSMS дээр ажиллуулна уу.
          </span>
        }
      />
    );
  }

  const canApprove = (summary?.exec_count || 0) > 0;

  return (
    <div>
      {/* ── Хураангуй — хувь + төлөв ─────────────────────────────── */}
      <div style={{ marginBottom: 10 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 13, color: "#334155" }}>
            Гүйцэтгэл, хяналт
            <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 11 }}>
              {" "}
              · {items.length} бичилт
            </span>
          </span>
          {summary ? (
            <Tag
              style={{
                margin: 0,
                fontSize: 11,
                color: summary.state_color,
                borderColor: summary.state_color,
                background: `${summary.state_color}14`,
              }}
            >
              {summary.state_label}
            </Tag>
          ) : null}
        </div>
        <Bar percent={summary?.percent ?? 0} />
      </div>

      {/* ── Үйлдлүүд ─────────────────────────────────────────────── */}
      {!readonly ? (
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          <Button size="small" type="primary" onClick={() => openAdd("EXEC")}>
            <i className="fas fa-wrench" style={{ marginRight: 4 }} />
            Гүйцэтгэл бүртгэх
          </Button>
          {/* ⚠ Гүйцэтгэлгүй үед товчийг УНТРААНА — сервер ч хориглодог
              тул дарахад зөвхөн улаан алдаа гарна. Яагаад болохгүйг
              tooltip-оор ТАЙЛБАРЛАНА, зүгээр л сааруулахгүй. */}
          <Tooltip
            title={
              canApprove
                ? ""
                : "Гүйцэтгэл бичигдээгүй байна — хянах зүйл алга"
            }
          >
            <Button
              size="small"
              disabled={!canApprove}
              onClick={() => openAdd("APPROVE")}
            >
              <i className="fas fa-check-circle" style={{ marginRight: 4 }} />
              Хянасан
            </Button>
          </Tooltip>
          <Tooltip
            title={
              canApprove
                ? ""
                : "Гүйцэтгэл бичигдээгүй байна — буцаах зүйл алга"
            }
          >
            <Button
              size="small"
              danger
              disabled={!canApprove}
              onClick={() => openAdd("REJECT")}
            >
              <i className="fas fa-undo" style={{ marginRight: 4 }} />
              Дахин гүйцэтгэх
            </Button>
          </Tooltip>
        </div>
      ) : null}

      {/* ── Түүх ─────────────────────────────────────────────────── */}
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: "#B0B7C3" }}>
          {loading ? "Ачааллаж…" : "Гүйцэтгэл хараахан бүртгэгдээгүй."}
        </div>
      ) : (
        <div>
          {items.map((r) => {
            const meta = TYPE_META[r.entry_type] || {
              label: r.entry_type,
              color: "#94A3B8",
              icon: "fa-circle",
            };
            const mine = String(r.executor_tn || "") === String(myTn || "");
            return (
              <div
                key={r.id}
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "6px 0",
                  borderTop: "1px solid #F1F2F4",
                }}
              >
                <span
                  style={{
                    width: 3,
                    alignSelf: "stretch",
                    background: meta.color,
                    borderRadius: 2,
                    flex: "0 0 auto",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <i
                      className={"fas " + meta.icon}
                      style={{ color: meta.color, fontSize: 11 }}
                    />
                    <b style={{ fontSize: 12, color: meta.color }}>
                      {meta.label}
                    </b>
                    {r.status_percent != null ? (
                      <Tag style={{ margin: 0, fontSize: 10 }}>
                        {r.status_percent}%
                      </Tag>
                    ) : null}
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>
                      {r.exec_date_text}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#475569",
                      whiteSpace: "pre-line",
                      wordBreak: "break-word",
                    }}
                  >
                    {r.exec_description || r.comment || "—"}
                  </div>
                  {r.exec_description && r.comment ? (
                    <div style={{ fontSize: 11.5, color: "#8A94A6" }}>
                      {r.comment}
                    </div>
                  ) : null}
                  <div style={{ fontSize: 11, color: "#94A3B8" }}>
                    {r.executor_name}
                    {r.executor_position ? ` · ${r.executor_position}` : ""}
                  </div>
                </div>
                {/* ⚠ ЗӨВХӨН ӨӨРИЙН бичилтийг устгана — сервер ч мөн
                    адил шалгана. Зөрчлийн гүйцэтгэл бол баримт. */}
                {!readonly && mine ? (
                  <Tooltip title="Устгах">
                    <Button
                      size="small"
                      type="text"
                      danger
                      onClick={() => remove(r)}
                    >
                      <i className="fas fa-times" style={{ fontSize: 11 }} />
                    </Button>
                  </Tooltip>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Нэмэх модал ──────────────────────────────────────────── */}
      <Modal
        open={!!adding}
        title={(TYPE_META[adding] || {}).label || "Бичилт"}
        onCancel={() => setAdding(null)}
        onOk={submit}
        okText="Хадгалах"
        cancelText="Болих"
        confirmLoading={busy}
        okButtonProps={{
          danger: adding === "REJECT",
          disabled:
            adding === "EXEC"
              ? !desc.trim()
              : adding === "REJECT"
                ? !note.trim()
                : false,
        }}
        destroyOnClose
        width={520}
      >
        {adding === "EXEC" ? (
          <>
            <div style={{ fontSize: 12, color: "#8A94A6" }}>
              Гүйцэтгэлийн хувь
            </div>
            <Slider
              min={0}
              max={100}
              step={5}
              value={percent}
              onChange={setPercent}
              marks={{ 0: "0", 50: "50", 100: "100%" }}
            />
            <div style={{ fontSize: 12, color: "#8A94A6", marginTop: 10 }}>
              Юу хийсэн бэ <b style={{ color: "#DC2626" }}>*</b>
            </div>
            <TextArea
              rows={3}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Авсан арга хэмжээгээ тодорхой бичнэ үү"
            />
            <div style={{ fontSize: 12, color: "#8A94A6", marginTop: 6 }}>
              Нэмэлт тайлбар
            </div>
            <TextArea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        ) : (
          <>
            {adding === "REJECT" ? (
              <Alert
                type="warning"
                showIcon
                style={{ fontSize: 12, marginBottom: 8 }}
                message="Гүйцэтгэгч юуг засахаа мэдэх ёстой"
                description="Юу дутуу байгааг тодорхой бичнэ үү — «дутуу» гэсэн ганц үг ажлыг урагшлуулахгүй."
              />
            ) : null}
            <div style={{ fontSize: 12, color: "#8A94A6" }}>
              {adding === "REJECT"
                ? "Буцаах шалтгаан"
                : "Хянасны тайлбар"}
              {adding === "REJECT" ? (
                <b style={{ color: "#DC2626" }}> *</b>
              ) : null}
            </div>
            <TextArea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
